/**
 * Field configuration for the User Management create and edit forms (Contact-User, CTU-1.1).
 */
import { CONTACT_STATUS_VALUES } from '@app-types/user';
import { ADDITIONAL_CONTACT_DATA_KEYS } from '@utils/userForm';
import type { FormField } from '@components/common/forms';
import type { Role } from '@app-types/admin';
import type { DivisionOption } from '@app-types/division';
import type {
  CanLoginFieldState,
  EditUserFormFieldsContext,
  UserFormFieldsContext,
  UserIdFieldState
} from '@app-types/userForm';

const CONTACT_STATUS_OPTIONS = CONTACT_STATUS_VALUES.map((status) => ({ value: status, label: status }));

const userIdField = ({ disabled, placeholder }: UserIdFieldState): FormField => ({
  name: 'user_id',
  label: 'UserID',
  type: 'text',
  required: true,
  disabled,
  placeholder
});

const preferredNameField = (canEdit: boolean): FormField => ({
  name: 'preferred_name',
  label: 'Preferred Name',
  type: 'text',
  required: false,
  // PreferredName is stored for an Admin only (D-010); for anyone else the field is disabled and not sent.
  disabled: !canEdit,
  placeholder: canEdit ? undefined : 'Editable by Admin only'
});

const additionalContactDataFields: FormField[] = ADDITIONAL_CONTACT_DATA_KEYS.map(({ formKey }, index) => ({
  name: formKey,
  label: `Additional Contact Data ${index + 1}`,
  type: 'text',
  required: false
}));

const rolesField = (roles: Role[]): FormField => ({
  name: 'role_ids',
  label: 'Roles',
  type: 'multiselect',
  options: roles.map((role) => ({ value: role.id.toString(), label: role.name })),
  placeholder: 'Select roles (leave empty for Contact)'
});

const divisionField = (divisions: DivisionOption[], visible: boolean): FormField => ({
  name: 'division_id',
  label: 'Division',
  type: 'select',
  required: visible,
  hidden: !visible,
  options: divisions.map((div) => ({ value: div.id.toString(), label: div.name }))
});

// Saved in the same request as the record, and shown in its Notes and History (ADO-811).
const noteField: FormField = {
  name: 'note',
  label: 'Note',
  type: 'text',
  required: false,
  placeholder: 'Optional note saved with the change'
};

const canLoginField = ({ disabled, placeholder }: CanLoginFieldState): FormField => ({
  name: 'can_login',
  label: 'Login Access',
  type: 'select',
  disabled,
  placeholder,
  options: [
    { value: 'true', label: 'Enabled' },
    { value: 'false', label: 'Disabled' }
  ]
});

/** Create form: every Contact-User required field is required, including UserID (defaults to NON-USER). */
export const buildCreateUserFormFields = (ctx: UserFormFieldsContext): FormField[] => [
  { name: 'contact_id', label: 'ContactID', type: 'text', required: true },
  userIdField(ctx.userId),
  {
    name: 'contact_status',
    label: 'Contact Status',
    type: 'select',
    required: true,
    placeholder: 'Select status',
    options: CONTACT_STATUS_OPTIONS
  },
  { name: 'email', label: 'Email', type: 'email', required: true },
  { name: 'first_name', label: 'First Name', type: 'text', required: true },
  { name: 'last_name', label: 'Last Name', type: 'text', required: true },
  preferredNameField(ctx.canEditPreferredName),
  // Department is free text and does not set Division (D-003)
  { name: 'department', label: 'Department', type: 'text', required: true },
  { name: 'cost_center', label: 'CostCenter', type: 'text', required: true },
  ...additionalContactDataFields,
  rolesField(ctx.roles),
  divisionField(ctx.divisions, ctx.divisionVisible),
  { name: 'auth_source', label: 'Auth Method', type: 'readonly', disabled: true },
  canLoginField(ctx.canLogin),
  noteField
];

// Per-user restrictions on the edit form's fields.
const restrictEditField = (field: FormField, ctx: EditUserFormFieldsContext): FormField => {
  const { selectedUser, isEditingSelf } = ctx;
  // Under MSAL sign-in Entra owns a Microsoft user's email; under Keycloak sign-in the change
  // is pushed to the user's Keycloak account (CTU-1.6).
  if (field.name === 'email' && ctx.isMsalProvider && selectedUser?.auth_source === 'microsoft') {
    return { ...field, type: 'readonly', disabled: true, placeholder: 'Email is managed in the Microsoft directory' };
  }
  if (field.name === 'role_ids' && isEditingSelf) {
    return { ...field, disabled: true, placeholder: 'You cannot change your own roles' };
  }
  // role_ids is NOT disabled for a user with dependencies: the backend validates which
  // roles can be removed; new roles can be added freely.

  // Setting Contact Status Inactive would deactivate a user with dependencies.
  if (field.name === 'contact_status' && selectedUser?.is_active && selectedUser?.dependencies?.hasDependencies) {
    return { ...field, disabled: true, placeholder: 'Cannot deactivate: User has active dependencies' };
  }
  if (field.name === 'can_login' && selectedUser?.can_login && selectedUser?.dependencies?.hasDependencies) {
    return { ...field, disabled: true, placeholder: 'Cannot disable: User has active dependencies' };
  }
  return field;
};

/**
 * Edit form. Department and CostCenter are not required on the client:
 * legacy rows can be blank (R2, NFR-5). The server rejects an empty value that is sent.
 * ContactID and UserID are required (ADO-1165).
 */
export const buildEditUserFormFields = (ctx: EditUserFormFieldsContext): FormField[] => {
  const fields: FormField[] = [
    { name: 'contact_id', label: 'ContactID', type: 'text', required: true },
    userIdField(ctx.userId),
    {
      name: 'contact_status',
      label: 'Contact Status',
      type: 'select',
      required: true,
      options: CONTACT_STATUS_OPTIONS
    },
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'first_name', label: 'First Name', type: 'text', required: true },
    { name: 'last_name', label: 'Last Name', type: 'text', required: true },
    preferredNameField(ctx.canEditPreferredName),
    { name: 'department', label: 'Department', type: 'text', required: false },
    { name: 'cost_center', label: 'CostCenter', type: 'text', required: false },
    ...additionalContactDataFields,
    rolesField(ctx.roles),
    divisionField(ctx.divisions, ctx.divisionVisible),
    // Auth Method is shown, never edited, on the edit form.
    { name: 'auth_source', label: 'Auth Method', type: 'readonly', disabled: true, required: true },
    canLoginField(ctx.canLogin),
    noteField
  ];
  return fields.map((field) => restrictEditField(field, ctx));
};
